// =============================================================================
// TeamWeekScreen — /hr/team-week?week=YYYY-MM-DD
// =============================================================================
// T-311 — restores manager visibility into logged-but-not-yet-submitted
// timesheets after T-276 made /timesheets personal-only-for-every-role.
//
// Read-only grid: rows = active employees, columns = days of the week.
// Each cell shows total hours + project code(s). Click a cell to see the
// per-entry breakdown in a popover (start/finish, project, client, notes,
// travel km+hours).
//
// CSV export = full row-level dump of every timesheet_entry visible.
// Replaces the planned T-295 timesheet-CSV path for this surface.
//
// No approve buttons here — approvals stay at /hr/timesheet-approvals.
//
// 5 Oct 2026 (mig 421 — Josh's approved depot day that should have been sick
// leave). The day popover now shows WHO entered each line and when (created_by
// → the person's name), a director can un-approve one approved worked entry
// (reason required, goes on the record; it drops back to draft so the × can
// remove it), and a manager can lodge leave for the person on that day
// (app.lodge_leave_for — lodged on their behalf, approved through the normal
// leave approval, so the 7.6 h / 0 h leave row is written the 383/384 way).
//
// 22 Oct 2026 (mig 443 — the King's Birthday pay run: everyone logged a depot
// day on the holiday and got double time; Ops had no way to put a public
// holiday NOT worked on anyone's timesheet). A gazetted QLD/NATIONAL holiday
// now reads amber in the week header and down its column, with the name. A
// manager has a one-click "Public holiday for everyone" on that column
// (app.public_holiday_for_everyone: PUB 7.6 h ordinary for every full/part-
// timer with nothing worked that day; anyone with hours is named back, never
// overwritten). The day popover offers the same for one person, and a
// "remove public-holiday day" on a PUB row (app.cancel_approved_leave) for
// the one who really did work it — the worked hours then book double time.
// =============================================================================
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, ChevronLeft, ChevronRight, Calendar, Download,
  Users, AlertTriangle, X, Wand2, CheckCircle2, Sun,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Button, Card, Pill, Spinner, EmptyState, KPI } from "@/components/ui";
import { format, parseISO, addDays, subDays, startOfWeek } from "date-fns";

const DEFAULT_LABOUR_CODE = "1010-LAB"; // T-459 — matches the timesheet wizard default labour code.

// ---- Types -----------------------------------------------------------------

interface EmployeeRow {
  employee_id: string;
  first_name:  string | null;
  last_name:   string | null;
  primary_role_name: string | null;
}

interface EntryRow {
  entry_id:        string;
  employee_id:     string;
  work_date:       string;        // YYYY-MM-DD
  project_id:      string | null;
  budget_code:     string | null;
  start_time:      string | null;
  end_time:        string | null;
  break_minutes:   number | null;
  total_hours:     number;
  hours_std:       number;
  hours_th:        number;
  hours_dt:        number;
  lafha_nights:    number | null;
  lafha_amount:    number | null;
  travel_km:       number | null;
  travel_hours:    number | null;
  notes:           string | null;
  submitted_at:    string | null;
  submitted_by:    string | null;   // T-499 — WHO pressed submit
  created_by:      string | null;
  created_at:      string | null;   // mig 421 — shown with who entered it
  rejection_notes: string | null;   // mig 421 — "Un-approved: …" / send-back reason
  mirrored_from_employee_id: string | null;   // set by Fill gaps
  approved_at:     string | null;
  leave_type_code: string | null;   // mig 383 — an approved leave day (project null, 7.6 h paid / 0 h unpaid)
  leave_request_id: string | null;
  projects: { project_code: string; project_name: string; client_id: string | null } | null;
}

interface ClientLookup { client_id: string; legal_name: string }

// mig 443 — a gazetted public holiday in the week (QLD or NATIONAL)
interface HolidayRow { holiday_date: string; holiday_name: string; state: string }

type CellEntries = EntryRow[];

// ---- Helpers ---------------------------------------------------------------

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function mondayOf(input?: string): string {
  const d = input ? parseISO(input) : new Date();
  return isoDate(startOfWeek(d, { weekStartsOn: 1 }));
}

function weekDates(mondayIso: string): string[] {
  const m = parseISO(mondayIso);
  return Array.from({ length: 7 }, (_, i) => isoDate(addDays(m, i)));
}

function empName(e: { first_name: string | null; last_name: string | null }): string {
  return [e.first_name, e.last_name].filter(Boolean).join(" ") || "Unnamed";
}

function csvEscape(v: any): string {
  if (v == null) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// ---- Component -------------------------------------------------------------

export default function TeamWeekScreen() {
  const user = useRequireAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [popover, setPopover] = useState<{ employee_id: string; date: string } | null>(null);

  const canSee = hasRole(user, "manager");
  const canFill = hasRole(user, "director");   // hidden bulk-fill — directors only
  const canQuickFill = hasRole(user, "manager"); // T-459 — directors/managers fill a cell inline
  const qc = useQueryClient();
  const [fillOpen, setFillOpen] = useState(false);
  const initialWeek = useMemo(() => {
    const w = searchParams.get("week");
    return w && /^\d{4}-\d{2}-\d{2}$/.test(w) ? mondayOf(w) : mondayOf();
  }, [searchParams]);
  const [weekStart, setWeekStart] = useState<string>(initialWeek);
  const dates = useMemo(() => weekDates(weekStart), [weekStart]);
  const weekEnd = dates[6];

  function gotoWeek(iso: string) {
    setWeekStart(iso);
    setSearchParams({ week: iso }, { replace: true });
    setPopover(null);
  }

  // Active employees (always all — empty rows show who's not logging)
  const { data: employees = [], isLoading: loadingEmps } = useQuery({
    queryKey: ["team_week_employees"],
    enabled: canSee,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employees")
        .select("employee_id, first_name, last_name, primary_role_name")
        .eq("is_active", true)
        .order("first_name");
      if (error) throw error;
      return (data ?? []) as EmployeeRow[];
    },
  });

  // T-499 — user_id -> employee_id, so "did this person submit their own week"
  // is answerable. submitted_by is a USER; the grid is keyed by EMPLOYEE.
  const { data: userMap = { byUser: {} as Record<string, any> } } = useQuery({
    queryKey: ["team_week_users"],
    enabled: canSee,
    queryFn: async () => {
      const { data } = await supabase
        .from("users")
        .select("user_id, employee_id, login_email, employees:employee_id(first_name, last_name)");
      const byUser: Record<string, any> = {};
      (data ?? []).forEach((u: any) => {
        byUser[u.user_id] = {
          employee_id: u.employee_id,
          name: [u.employees?.first_name, u.employees?.last_name].filter(Boolean).join(" ")
                || u.login_email || "someone",
        };
      });
      return { byUser };
    },
  });

  // Entries for the week + projects joined
  const { data: entries = [], isLoading: loadingEntries } = useQuery({
    queryKey: ["team_week_entries", weekStart],
    enabled: canSee,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("timesheet_entries")
        .select(`
          entry_id, employee_id, work_date, project_id, budget_code,
          start_time, end_time, break_minutes, total_hours,
          hours_std, hours_th, hours_dt, is_night, lafha_nights, lafha_amount,
          travel_km, travel_hours, notes, submitted_at, approved_at,
          submitted_by, created_by, created_at, rejection_notes, mirrored_from_employee_id,
          leave_type_code, leave_request_id,
          projects:project_id(project_code, project_name, client_id)
        `)
        .gte("work_date", weekStart)
        .lte("work_date", weekEnd);
      if (error) throw error;
      return (data ?? []) as unknown as EntryRow[];
    },
  });

  // Client lookup — only the ones we need
  const clientIds = useMemo(
    () => Array.from(new Set(entries.map(e => e.projects?.client_id).filter(Boolean))) as string[],
    [entries],
  );
  const { data: clients = [] } = useQuery({
    queryKey: ["team_week_clients", clientIds.join(",")],
    enabled: clientIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("client_id, legal_name")
        .in("client_id", clientIds);
      if (error) throw error;
      return (data ?? []) as ClientLookup[];
    },
  });
  // public.clients has no client_name column — it never has. The name fields are
  // legal_name, trading_name and client_code, and legal_name is what the other 22
  // client displays in this app use. Selecting client_name made PostgREST reject
  // the whole query, so this lookup has been throwing and client names have been
  // silently missing on this screen in production. Found 15/08/2026 by the
  // typecheck, which supabase-js catches at the type level.
  const clientById = useMemo(() => new Map(clients.map(c => [c.client_id, c.legal_name])), [clients]);

  // Submit on behalf (manager+) — stamps an employee's week submitted so it flows
  // to Timesheet Approvals. Submit only; approval still happens there.
  const submitMut = useMutation({
    mutationFn: async (employeeId: string) => {
      const { data, error } = await supabase.schema("app").rpc("submit_timesheet_week_for", {
        p_week_start: weekStart, p_employee_id: employeeId,
      });
      if (error) throw error;
      return data as number;
    },
    onSuccess: (n) => {
      qc.invalidateQueries({ queryKey: ["team_week_entries", weekStart] });
      qc.invalidateQueries({ queryKey: ["timesheet_approvals_pending"] });
      if (n === 0) window.alert("Nothing to submit — no un-submitted entries for that person this week.");
    },
    onError: (e: any) => window.alert(e?.message ?? "Submit failed"),
  });

  // T-459 — active projects for the inline quick-fill picker.
  const { data: projects = [] } = useQuery({
    queryKey: ["team_week_projects"],
    enabled: canSee,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects").select("project_id, project_code, project_name")
        .in("status", ["live", "mobilising"])   // T-459 — live projects only, matches the wizard
        .order("project_code", { ascending: false });
      if (error) throw error;
      return (data ?? []) as { project_id: string; project_code: string; project_name: string | null }[];
    },
  });

  // mig 421 — leave types for "lodge leave for this day".
  const { data: leaveTypes = [] } = useQuery({
    queryKey: ["team_week_leave_types"],
    enabled: canQuickFill,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("leave_types").select("leave_type_code, display_name, is_paid").order("sort_order");
      if (error) throw error;
      return (data ?? []) as { leave_type_code: string; display_name: string; is_paid: boolean }[];
    },
  });

  // mig 443 — the week's public holidays (same table and shape the schedule
  // paints from; QLD or NATIONAL only, which is what the pay rules read).
  const { data: holidays = [] } = useQuery({
    queryKey: ["team_week_holidays", weekStart],
    enabled: canSee,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("holidays")
        .select("holiday_date, holiday_name, state")
        .gte("holiday_date", weekStart)
        .lte("holiday_date", weekEnd)
        .in("state", ["QLD", "NATIONAL"])
        .order("holiday_date");
      if (error) throw error;
      return (data ?? []) as HolidayRow[];
    },
  });
  const holidayByDate = useMemo(() => {
    const m = new Map<string, string>();
    for (const h of holidays) {
      const k = String(h.holiday_date).slice(0, 10);
      if (!m.has(k)) m.set(k, h.holiday_name);
    }
    return m;
  }, [holidays]);

  // mig 443 — "Public holiday for everyone": PUB 7.6 h ordinary for every
  // full/part-timer with nothing worked that day. Never overwrites hours.
  const pubEveryone = useMutation({
    mutationFn: async (date: string) => {
      const { data, error } = await (supabase as any)
        .schema("app").rpc("public_holiday_for_everyone", { p_date: date });
      if (error) throw error;
      return data as { holiday: string; date: string; written: number; already: number; skipped: string[] };
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["team_week_entries", weekStart] });
      qc.invalidateQueries({ queryKey: ["leave_requests"] });
      const lines = [
        `${r.holiday} — ${format(parseISO(r.date), "EEE dd MMM")}`,
        `Put on for ${r.written} ${r.written === 1 ? "person" : "people"} (7.6 h public holiday, ordinary).`,
        r.already > 0 ? `${r.already} already had it.` : "",
        r.skipped?.length
          ? `Skipped — hours already on the day, open their cell to sort it out:\n  ${r.skipped.join("\n  ")}`
          : "",
      ].filter(Boolean);
      window.alert(lines.join("\n"));
    },
    onError: (e: any) => window.alert(e?.message ?? "Could not put the public holiday on."),
  });

  // mig 443 — the person DID work the holiday: take the PUB day off again
  // (app.cancel_approved_leave — deletes the row, marks the request cancelled).
  const removePub = useMutation({
    mutationFn: async (v: { request_id: string; reason: string }) => {
      const { error } = await (supabase as any)
        .schema("app").rpc("cancel_approved_leave", { p_request_id: v.request_id, p_reason: v.reason });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["team_week_entries", weekStart] });
      qc.invalidateQueries({ queryKey: ["leave_requests"] });
    },
    onError: (e: any) => window.alert(e?.message ?? "Could not remove the public-holiday day."),
  });

  // mig 421 — a director takes one approved worked entry back to draft.
  const unapprove = useMutation({
    mutationFn: async (v: { entry_id: string; reason: string }) => {
      const { error } = await (supabase as any)
        .schema("app").rpc("timesheet_entry_unapprove", { p_entry_id: v.entry_id, p_reason: v.reason });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["team_week_entries", weekStart] });
      qc.invalidateQueries({ queryKey: ["timesheet_approvals_pending"] });
    },
    onError: (e: any) => window.alert(e?.message ?? "Could not un-approve that entry."),
  });

  // mig 421 — lodge (and approve) leave for the person on one day.
  const lodgeLeave = useMutation({
    mutationFn: async (v: { employee_id: string; work_date: string; leave_type_code: string; reason: string }) => {
      const { error } = await (supabase as any)
        .schema("app").rpc("lodge_leave_for", {
          p_employee_id: v.employee_id, p_leave_type_code: v.leave_type_code,
          p_starts_on: v.work_date, p_ends_on: v.work_date, p_reason: v.reason || null,
        });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["team_week_entries", weekStart] });
      qc.invalidateQueries({ queryKey: ["leave_requests"] });
    },
    onError: (e: any) => window.alert(e?.message ?? "Could not lodge the leave."),
  });

  // T-459 — inline add: fill a person's day without the wizard (manager+ only; RLS enforces).
  const addEntry = useMutation({
    mutationFn: async (v: { employee_id: string; work_date: string; project_id: string; hours: number; taNight: boolean; isNight: boolean }) => {
      const { data: periodId, error: pe } = await supabase
        .schema("app").rpc("ensure_timesheet_period", { p_date: v.work_date });
      if (pe) throw pe;
      const { error } = await (supabase as any).from("timesheet_entries").insert({
        period_id:    periodId as string,
        employee_id:  v.employee_id,
        work_date:    v.work_date,
        project_id:   v.project_id || null,
        budget_code:  DEFAULT_LABOUR_CODE,
        total_hours:  v.hours,
        lafha_nights: v.taNight ? 1 : 0,
        is_night:     v.isNight,  // mig 350 — trigger books all hours DT
        notes:        `[logged by manager on ${new Date().toISOString().slice(0, 10)}]`,
      });
      if (error) {
        if ((error as any).code === "23505") throw new Error("That person already has an entry for this project today.");
        throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["team_week_entries", weekStart] }),
    onError: (e: any) => window.alert(e?.message ?? "Could not add the entry."),
  });

  // T-491 — quick-add always inserts, so a mis-keyed 10h day fixed to 8h leaves
  // two entries rather than one. Until the day view can edit in place, the honest
  // fix is to let a manager remove a draft. Submitted or approved rows are never
  // deletable here — those go back through the approval trail.
  const removeEntry = useMutation({
    mutationFn: async (entryId: string) => {
      const { error } = await supabase
        .from("timesheet_entries")
        .delete()
        .eq("entry_id", entryId)
        .is("submitted_at", null)
        .is("approved_at", null);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["team_week_entries", weekStart] }),
    onError: (e: any) => window.alert(e?.message ?? "Could not remove that entry."),
  });

  // Index entries: employee_id → date → EntryRow[]
  const grid = useMemo(() => {
    const m = new Map<string, Map<string, EntryRow[]>>();
    for (const e of entries) {
      const byEmp = m.get(e.employee_id) ?? new Map<string, EntryRow[]>();
      const cell = byEmp.get(e.work_date) ?? [];
      cell.push(e);
      byEmp.set(e.work_date, cell);
      m.set(e.employee_id, byEmp);
    }
    return m;
  }, [entries]);

  // KPIs
  const kpis = useMemo(() => {
    const all = entries;
    const hours = all.reduce((a, e) => a + (Number(e.total_hours) || 0), 0);
    // mig 384 — paid leave and ordinary hours go to different pay items, so the
    // week is shown split. A leave row is the one written by a leave approval.
    const isLeave = (e: EntryRow) => !!e.leave_request_id;
    const leaveHours  = all.filter(isLeave).reduce((a, e) => a + (Number(e.total_hours) || 0), 0);
    const workedHours = all.filter(e => !isLeave(e)).reduce((a, e) => a + (Number(e.total_hours) || 0), 0);
    const leaveDays   = all.filter(isLeave).length;
    const onLeave     = new Set(all.filter(isLeave).map(e => e.employee_id)).size;
    const ot    = all.reduce((a, e) => a + (Number(e.hours_th) || 0) + (Number(e.hours_dt) || 0), 0);
    const travelKm = all.reduce((a, e) => a + (Number(e.travel_km) || 0), 0);
    const travelHrs = all.reduce((a, e) => a + (Number(e.travel_hours) || 0), 0);
    const taNights = all.reduce((a, e) => a + (Number(e.lafha_nights) || 0), 0); // T-325
    const peopleLogged = new Set(all.map(e => e.employee_id)).size;
    const submittedRows = all.filter(e => e.submitted_at).length;
    return { hours, ot, travelKm, travelHrs, taNights, peopleLogged, submittedRows, totalRows: all.length,
             leaveHours, workedHours, leaveDays, onLeave };
  }, [entries]);

  // ---- CSV export ---------------------------------------------------------
  function downloadCsv() {
    const cols = [
      "Employee", "Role", "Work date", "Day", "Project code", "Project name", "Client",
      "Where", "Leave type", "Start", "Finish", "Break (min)", "Total hours", "STD", "TH (1.5x)", "DT (2x)",
      "Travel km", "Travel hours", "LAFHA nights", "LAFHA $",
      "Submitted at", "Approved at", "Notes",
    ];
    const rows = entries
      .slice()
      .sort((a, b) => a.work_date.localeCompare(b.work_date) || a.employee_id.localeCompare(b.employee_id))
      .map(e => {
        const emp = employees.find(x => x.employee_id === e.employee_id);
        const clientName = e.projects?.client_id ? clientById.get(e.projects.client_id) : "";
        const where = e.leave_request_id ? "Leave" : e.project_id ? "Site" : "Depot";
        const dayName = format(parseISO(e.work_date), "EEE");
        return [
          emp ? empName(emp) : e.employee_id,
          emp?.primary_role_name ?? "",
          e.work_date,
          dayName,
          e.projects?.project_code ?? "",
          e.projects?.project_name ?? "",
          clientName ?? "",
          where,
          e.leave_type_code ?? "",
          e.start_time ?? "",
          e.end_time ?? "",
          e.break_minutes ?? "",
          (Number(e.total_hours) || 0).toFixed(2),
          (Number(e.hours_std) || 0).toFixed(2),
          (Number(e.hours_th)  || 0).toFixed(2),
          (Number(e.hours_dt)  || 0).toFixed(2),
          e.travel_km ?? "",
          e.travel_hours ?? "",
          e.lafha_nights ?? "",
          e.lafha_amount ?? "",
          e.submitted_at ?? "",
          e.approved_at ?? "",
          e.notes ?? "",
        ];
      });
    const csv = [cols, ...rows].map(line => line.map(csvEscape).join(",")).join("\n");
    // BOM for Excel UTF-8 compat
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `team_week_${weekStart}_to_${weekEnd}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // ---- Render --------------------------------------------------------------

  if (!user) return null;
  if (!canSee) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <EmptyState
            title="Manager+ only"
            description="The team week view is restricted to managers and directors."
            icon={AlertTriangle}
          />
        </Card>
      </div>
    );
  }

  const loading = loadingEmps || loadingEntries;
  const popoverEntries = popover ? (grid.get(popover.employee_id)?.get(popover.date) ?? []) : [];

  return (
    <div className="px-6 md:px-10 py-6 pb-16 max-w-[1600px] mx-auto">
      <Link to="/hr" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3">
        <ArrowLeft size={14} /> HR hub
      </Link>

      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="text-text-muted text-[11px] uppercase tracking-wider2 font-mono mb-1">HR portal</div>
          <h1 className="text-[24px] font-semibold tracking-tight flex items-center gap-2">
            <Users size={22} className="text-accent" /> Team week
          </h1>
          <p className="text-text-muted text-[13px] mt-1">
            What the crew is logging this week — including drafts not yet submitted. Approve at{" "}
            <Link to="/hr/timesheet-approvals" className="text-accent hover:underline">Timesheet approvals</Link>.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="secondary" size="sm" icon={ChevronLeft} onClick={() => gotoWeek(mondayOf(isoDate(subDays(parseISO(weekStart), 7))))}>Prev</Button>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-ink-raised border border-line rounded-md">
            <Calendar size={13} className="text-text-muted" />
            <span className="text-[13px] font-mono">
              {format(parseISO(weekStart), "dd MMM")} – {format(parseISO(weekEnd), "dd MMM yyyy")}
            </span>
          </div>
          <Button variant="secondary" size="sm" onClick={() => gotoWeek(mondayOf(isoDate(addDays(parseISO(weekStart), 7))))}>
            Next <ChevronRight size={12} />
          </Button>
          <input
            type="date"
            value={weekStart}
            onChange={e => gotoWeek(mondayOf(e.target.value))}
            className="px-2 py-1.5 rounded border border-line bg-ink-card text-text-muted text-[12px]"
          />
          <Button icon={Download} size="sm" onClick={downloadCsv} disabled={entries.length === 0}>
            Export CSV
          </Button>
          {canFill && (
            <Button variant="secondary" size="sm" icon={Wand2} onClick={() => setFillOpen(true)}
              title="Director only — copy a crew lead's week onto empty crew-mates">
              Fill gaps
            </Button>
          )}
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-5">
        <KPI label="People logged"   value={`${kpis.peopleLogged} / ${employees.length}`} sub="active staff" />
        <KPI label="Hours total"     value={kpis.hours.toFixed(1)}
             sub={kpis.leaveHours > 0 ? `${kpis.workedHours.toFixed(1)} worked + ${kpis.leaveHours.toFixed(1)} leave` : undefined} />
        <KPI label="Leave"           value={`${kpis.leaveHours.toFixed(1)}h`}
             sub={kpis.leaveDays > 0 ? `${kpis.leaveDays} day${kpis.leaveDays === 1 ? "" : "s"} · ${kpis.onLeave} ${kpis.onLeave === 1 ? "person" : "people"}` : "nobody on leave"} />
        <KPI label="Overtime"        value={`${kpis.ot.toFixed(1)}h`} accent={kpis.ot > 0} />
        <KPI label="TA nights"       value={kpis.taNights} sub="LAFHA claimed" accent={kpis.taNights > 0} />
        <KPI label="Travel"          value={`${kpis.travelHrs.toFixed(1)}h`} sub={`${kpis.travelKm.toFixed(0)} km`} />
        <KPI label="Entries"         value={kpis.totalRows} />
        <KPI label="Submitted"       value={`${kpis.submittedRows} / ${kpis.totalRows}`} />
      </div>

      {/* Grid */}
      <Card>
        {loading ? (
          <div className="p-10 flex justify-center"><Spinner size={20} /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono border-b border-line">
                  <th className="text-left py-2 pl-4 pr-3 sticky left-0 bg-ink-card z-10">Employee</th>
                  {dates.map(d => {
                    // mig 443 — a public holiday reads amber, with its name and
                    // the one-click for everyone (manager+). Weekend holidays
                    // (Easter Saturday) are named but get no button — nothing
                    // to put on a timesheet.
                    const hol = holidayByDate.get(d);
                    const weekday = parseISO(d).getDay() >= 1 && parseISO(d).getDay() <= 5;
                    return (
                      <th key={d} className={`text-center py-2 px-1 min-w-[88px] align-top ${hol ? "bg-warn/15 shadow-[inset_0_-2px_0_rgba(234,179,8,0.85)]" : ""}`}
                          title={hol ? `Public holiday — ${hol}` : undefined}>
                        {format(parseISO(d), "EEE")}<br/>
                        <span className="font-normal text-text-dim">{format(parseISO(d), "dd/MM")}</span>
                        {hol && (
                          <div className="mt-1 normal-case tracking-normal font-sans">
                            <div className="text-[10px] text-warn leading-tight flex items-center justify-center gap-1">
                              <Sun size={10} /> {hol}
                            </div>
                            {canQuickFill && weekday && (
                              <button type="button" disabled={pubEveryone.isPending}
                                onClick={() => {
                                  if (window.confirm(`${hol} (${format(parseISO(d), "EEE dd MMM")})\n\nPut a 7.6 h public holiday on for every full-time and part-time employee who has nothing logged that day?\n\nAnyone with hours already on the day is skipped and named — nobody's time is overwritten.`)) pubEveryone.mutate(d);
                                }}
                                title="Public holiday (7.6 h, ordinary) for everyone with nothing worked that day"
                                className="mt-1 px-1.5 py-0.5 rounded border border-warn/50 bg-warn/10 text-warn text-[10px] hover:bg-warn/20 disabled:opacity-50">
                                {pubEveryone.isPending ? "Putting on…" : "Public holiday for everyone"}
                              </button>
                            )}
                          </div>
                        )}
                      </th>
                    );
                  })}
                  <th className="text-right py-2 px-3">Total</th>
                  <th className="text-right py-2 px-3">TA</th>
                  <th className="text-left py-2 px-3 pr-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {employees.map(emp => {
                  const byDate = grid.get(emp.employee_id);
                  const empTotal = byDate
                    ? Array.from(byDate.values()).flat().reduce((a, e) => a + (Number(e.total_hours) || 0), 0)
                    : 0;
                  // T-325 — per-person TA/LAFHA nights for the week, so a missing
                  // night stands out at a glance before payroll.
                  const empNights = byDate
                    ? Array.from(byDate.values()).flat().reduce((a, e) => a + (Number(e.lafha_nights) || 0), 0)
                    : 0;
                  const anyEntry = !!byDate && byDate.size > 0;
                  const allSubmitted = anyEntry
                    && Array.from(byDate!.values()).flat().every(e => e.submitted_at);
                  const allApproved = anyEntry
                    && Array.from(byDate!.values()).flat().every(e => e.approved_at);
                  // T-499 — who submitted, and where the rows came from. A week the
                  // crew member submitted himself is a different thing from one a
                  // director submitted for him, and different again from rows Fill
                  // gaps copied off a crew lead. Same hours, different confidence.
                  const flat = anyEntry ? Array.from(byDate!.values()).flat() : [];
                  const submitters = Array.from(new Set(
                    flat.map(e => e.submitted_by).filter(Boolean))) as string[];
                  const selfSubmitted = submitters.length > 0
                    && submitters.every(uid => userMap.byUser[uid]?.employee_id === emp.employee_id);
                  const submitterName = submitters.length === 1
                    ? (userMap.byUser[submitters[0]]?.name ?? null) : null;
                  const filledCount = flat.filter(e => e.mirrored_from_employee_id).length;

                  const status: { label: string; tone: "approved" | "pending" | "notice" | "default" } =
                    !anyEntry      ? { label: "no entries",  tone: "default"  } :
                    allApproved    ? { label: "approved",    tone: "approved" } :
                    allSubmitted   ? (submitters.length === 0
                                       ? { label: "submitted", tone: "pending" }
                                       : selfSubmitted
                                         ? { label: "self-submitted", tone: "approved" }
                                         : { label: "submitted for them", tone: "notice" })
                                   : { label: "in progress", tone: "pending"  };
                  return (
                    <tr key={emp.employee_id} className="border-b border-line/40 hover:bg-ink-hover">
                      <td className="py-2 pl-4 pr-3 sticky left-0 bg-ink-card z-10">
                        <div className="text-text">{empName(emp)}</div>
                        {emp.primary_role_name && (
                          <div className="text-[10px] text-text-muted font-mono">{emp.primary_role_name.replace(/_/g, " ")}</div>
                        )}
                      </td>
                      {dates.map(d => {
                        const cellEntries: CellEntries = byDate?.get(d) ?? [];
                        const cellHours = cellEntries.reduce((a, e) => a + (Number(e.total_hours) || 0), 0);
                        const cellNights = cellEntries.reduce((a, e) => a + (Number(e.lafha_nights) || 0), 0); // T-325
                        // mig 383 — leave days show as the leave code (AL / LWOP…) not DEPOT
                        const projectCodes = Array.from(new Set(cellEntries.map(e => e.leave_type_code ? e.leave_type_code : (e.projects?.project_code ?? (e.project_id ? "—" : "DEPOT"))))).filter(Boolean);
                        const hasEntry = cellEntries.length > 0;
                        const isLeave = hasEntry && cellEntries.every(e => e.leave_type_code);
                        const isApproved = hasEntry && cellEntries.every(e => e.approved_at);
                        // mig 443 — the holiday column stays amber; worked hours on
                        // it (all double time) read amber too so they stand out
                        const isHol = holidayByDate.has(d);
                        const workedOnHol = isHol && hasEntry && cellEntries.some(e => !e.leave_type_code && Number(e.total_hours) > 0);
                        const cellBg = !hasEntry ? (isHol ? "bg-warn/8" : "") : workedOnHol ? "bg-warn/20" : isLeave ? "bg-info/12" : isApproved ? "bg-ok/10" : "bg-ink-raised";
                        return (
                          <td key={d} className={`text-center py-1 px-1 align-top ${isHol ? "bg-warn/8" : ""}`}>
                            <button
                              type="button"
                              disabled={!hasEntry && !canQuickFill}
                              onClick={() => setPopover({ employee_id: emp.employee_id, date: d })}
                              className={`w-full rounded px-1 py-1.5 ${cellBg} ${(hasEntry || canQuickFill) ? "hover:ring-1 hover:ring-accent cursor-pointer" : "cursor-default"}`}
                            >
                              <div className={`font-mono ${hasEntry ? (workedOnHol ? "text-warn" : isLeave ? "text-info" : "text-text") : "text-text-dim"}`}
                                   title={workedOnHol ? "Hours worked on a public holiday — every hour pays double time" : undefined}>
                                {hasEntry ? cellHours.toFixed(1) : (canQuickFill ? "+" : "—")}
                              </div>
                              {projectCodes.length > 0 && (
                                <div className={`text-[9px] font-mono truncate mt-0.5 ${isLeave ? "text-info" : "text-text-muted"}`}>
                                  {projectCodes.slice(0, 2).join(", ")}{projectCodes.length > 2 ? "+" : ""}
                                </div>
                              )}
                              {cellNights > 0 && (
                                <div className="text-[9px] font-mono text-accent mt-0.5">
                                  TA{cellNights > 1 ? `·${cellNights}` : ""}
                                </div>
                              )}
                            </button>
                          </td>
                        );
                      })}
                      <td className="text-right py-2 px-3 font-mono text-text">{empTotal.toFixed(1)}</td>
                      <td className={`text-right py-2 px-3 font-mono ${empNights > 0 ? "text-accent" : "text-text-dim"}`}>
                        {anyEntry ? `${empNights}n` : "—"}
                      </td>
                      <td className="py-2 px-3 pr-4">
                        <div className="flex items-center gap-2">
                          <Pill label={status.label} tone={status.tone} small />
                          {/* Who, and how many rows were copied rather than entered. */}
                          {submitterName && !selfSubmitted && (
                            <span className="text-[10px] text-info" title={`Submitted by ${submitterName}`}>
                              by {submitterName.split(" ")[0]}
                            </span>
                          )}
                          {filledCount > 0 && (
                            <span className="text-[10px] text-warn"
                              title={`${filledCount} of ${flat.length} rows were copied from a crew lead by Fill gaps, not entered by this person`}>
                              {filledCount === flat.length ? "filled" : `${filledCount} filled`}
                            </span>
                          )}
                          {canSee && anyEntry && !allSubmitted && (
                            <button type="button" disabled={submitMut.isPending}
                              onClick={() => { if (window.confirm(`Submit ${empName(emp)}'s week for them? It'll go to Timesheet Approvals.`)) submitMut.mutate(emp.employee_id); }}
                              className="text-[11px] text-accent hover:underline disabled:opacity-50">
                              Submit
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Cell popover */}
      {popover && (popoverEntries.length > 0 || canQuickFill) && (
        <CellPopover
          entries={popoverEntries}
          date={popover.date}
          employeeName={(() => { const e = employees.find(x => x.employee_id === popover.employee_id); return e ? empName(e) : ""; })()}
          clientById={clientById}
          canAdd={canQuickFill}
          projects={projects}
          adding={addEntry.isPending}
          onAdd={(v) => addEntry.mutate({ ...v, employee_id: popover!.employee_id, work_date: popover!.date })}
          onRemove={(id) => removeEntry.mutate(id)}
          removing={removeEntry.isPending}
          byUser={userMap.byUser}
          isDirector={canFill}
          onUnapprove={(entry_id, reason) => unapprove.mutate({ entry_id, reason })}
          unapproving={unapprove.isPending}
          leaveTypes={leaveTypes}
          onLodgeLeave={(leave_type_code, reason) => lodgeLeave.mutate({ employee_id: popover!.employee_id, work_date: popover!.date, leave_type_code, reason })}
          lodging={lodgeLeave.isPending}
          holidayName={holidayByDate.get(popover.date) ?? null}
          onRemovePub={(request_id, reason) => removePub.mutate({ request_id, reason })}
          removingPub={removePub.isPending}
          onClose={() => setPopover(null)}
        />
      )}

      {fillOpen && canFill && (
        <FillGapsModal
          weekStart={weekStart}
          employees={employees}
          grid={grid}
          onClose={() => setFillOpen(false)}
          onDone={(n) => {
            setFillOpen(false);
            qc.invalidateQueries({ queryKey: ["team_week_entries", weekStart] });
            window.alert(`Filled ${n} day${n === 1 ? "" : "s"} across the crew — review on the grid, then Submit + approve.`);
          }}
        />
      )}
    </div>
  );
}

// ---- Fill-gaps modal (director-only) ---------------------------------------
function FillGapsModal({
  weekStart, employees, grid, onClose, onDone,
}: {
  weekStart: string;
  employees: EmployeeRow[];
  grid: Map<string, Map<string, EntryRow[]>>;
  onClose: () => void;
  onDone: (n: number) => void;
}) {
  const [sourceId, setSourceId] = useState("");
  const [targets, setTargets] = useState<Set<string>>(new Set());


  const withEntries = employees.filter(e => (grid.get(e.employee_id)?.size ?? 0) > 0);

  // Start with NO-ONE ticked — the director picks exactly who to fill.
  const sourceDays = sourceId ? (grid.get(sourceId)?.size ?? 0) : 0;

  const run = useMutation({
    mutationFn: async () => {
      if (!sourceId) throw new Error("Pick a crew lead to copy from");
      const tids = Array.from(targets).filter(t => t !== sourceId);
      if (tids.length === 0) throw new Error("Pick at least one person to fill");
      const { data, error } = await supabase.schema("app").rpc("fill_week_from_lead", {
        p_week_start: weekStart, p_source_employee_id: sourceId,
        // T-473 — deliberately NOT passing p_suppress_ot. Filling a crew-mate's
        // real worked days must book the award; flattening belongs only to the
        // Allan mirror (app.mirror_timesheet_week), where it is correct.
        p_target_employee_ids: tids,
      });
      if (error) throw error;
      return data as number;
    },
    onSuccess: (n) => onDone(n),
    onError: (e: any) => window.alert(e?.message ?? "Fill failed"),
  });

  function toggle(id: string) {
    const next = new Set(targets);
    if (next.has(id)) next.delete(id); else next.add(id);
    setTargets(next);
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-lg w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-line flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2"><Wand2 size={18} className="text-accent" /> Fill gaps from a crew lead</h3>
            <div className="text-[12px] text-text-muted mt-1">
              Copies the lead's week onto each person's <span className="text-text">empty days only</span> — never overwrites a day they've entered. Saved as draft, so review then Submit + approve.
            </div>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text p-1"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider2 text-text-muted">Copy from (crew lead)</span>
            <select value={sourceId} onChange={e => setSourceId(e.target.value)} className="mt-1 w-full bg-ink border border-line rounded px-2.5 py-2 text-[13px] text-text">
              <option value="">— pick who worked this week —</option>
              {withEntries.map(e => <option key={e.employee_id} value={e.employee_id}>{empName(e)} · {grid.get(e.employee_id)?.size ?? 0} days</option>)}
            </select>
          </label>
          <div>
            <span className="text-[11px] uppercase tracking-wider2 text-text-muted">Fill onto</span>
            <div className="mt-1 max-h-52 overflow-y-auto border border-line rounded p-2 space-y-0.5">
              {employees.filter(e => e.employee_id !== sourceId).map(e => {
                const days = grid.get(e.employee_id)?.size ?? 0;
                return (
                  <label key={e.employee_id} className="flex items-center gap-2 text-[13px] px-1 py-0.5">
                    <input type="checkbox" checked={targets.has(e.employee_id)} onChange={() => toggle(e.employee_id)} className="accent-accent" />
                    <span className="text-text">{empName(e)}</span>
                    <span className="text-[11px] text-text-muted ml-auto">{days > 0 ? `${days} day${days === 1 ? "" : "s"} in` : "empty"}</span>
                  </label>
                );
              })}
            </div>
            <div className="text-[11px] text-text-dim mt-1">People with days already in only get their empty days filled — existing entries are left alone.</div>
          </div>
          <div className="text-[11px] text-text-muted bg-ink-raised border border-line rounded px-3 py-2">
            Copied days are booked against the award, exactly like the week they came from — so the two weeks match in payroll.
          </div>
          {sourceId && (
            <div className="text-[12px] text-text-muted bg-ink-raised border border-line rounded px-3 py-2">
              Each ticked person gets up to <span className="text-text font-mono">{sourceDays}</span> day(s) copied onto their empty days, as draft.
            </div>
          )}
        </div>
        <div className="p-4 border-t border-line bg-ink-raised flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button icon={CheckCircle2} disabled={run.isPending || !sourceId} onClick={() => run.mutate()}>{run.isPending ? "Filling…" : "Fill empty days"}</Button>
        </div>
      </div>
    </div>
  );
}

// ---- Cell popover ----------------------------------------------------------

function CellPopover({
  entries, date, employeeName, clientById, canAdd, projects, adding, onAdd, onClose,
  onRemove, removing, byUser, isDirector, onUnapprove, unapproving, leaveTypes, onLodgeLeave, lodging,
  holidayName, onRemovePub, removingPub,
}: {
  entries: EntryRow[];
  date: string;
  employeeName: string;
  clientById: Map<string, string>;
  canAdd: boolean;
  projects: { project_id: string; project_code: string; project_name: string | null }[];
  adding: boolean;
  onAdd: (v: { project_id: string; hours: number; taNight: boolean; isNight: boolean }) => void;
  onClose: () => void;
  onRemove: (entryId: string) => void;
  removing: boolean;
  byUser: Record<string, { employee_id: string | null; name: string }>;
  isDirector: boolean;
  onUnapprove: (entryId: string, reason: string) => void;
  unapproving: boolean;
  leaveTypes: { leave_type_code: string; display_name: string; is_paid: boolean }[];
  onLodgeLeave: (leaveTypeCode: string, reason: string) => void;
  lodging: boolean;
  holidayName: string | null;                                   // mig 443 — set when the day is a public holiday
  onRemovePub: (requestId: string, reason: string) => void;    // mig 443 — they worked it: take the PUB day off
  removingPub: boolean;
}) {
  const dateStr = format(parseISO(date), "EEEE dd MMM yyyy");
  // mig 421 — lodge leave for this day
  const [leaveCode, setLeaveCode] = useState("");
  const [leaveReason, setLeaveReason] = useState("");
  const approvedWork = entries.filter(e => e.approved_at && !e.leave_request_id && !e.leave_type_code);
  const alreadyLeave = entries.some(e => e.leave_request_id || e.leave_type_code);
  // mig 443 — the public-holiday day is the only leave the office puts on
  // without a request, so the dropdown never offers it; the holiday gets its
  // own one-click instead.
  const pickableLeaveTypes = leaveTypes.filter(t => t.leave_type_code !== "PUB");
  const isWeekday = parseISO(date).getDay() >= 1 && parseISO(date).getDay() <= 5;
  const [projectId, setProjectId] = useState("");
  const [hours, setHours] = useState("10");
  const [taNight, setTaNight] = useState(false);
  const [isNight, setIsNight] = useState(false); // mig 350
  const [err, setErr] = useState<string | null>(null);
  function submitAdd() {
    const h = parseFloat(hours);
    if (isNaN(h) || h < 0) { setErr("Enter valid hours."); return; }
    setErr(null);
    onAdd({ project_id: projectId, hours: h, taNight, isNight });
    setHours("10"); setTaNight(false); setIsNight(false);
  }
  return (
    <div
      className="fixed inset-0 z-40 bg-black/40 flex items-start justify-center pt-20 px-4"
      onClick={onClose}
    >
      <div
        className="bg-ink-card border border-line rounded-lg shadow-xl w-full max-w-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-line">
          <div>
            <div className="text-[14px] font-semibold text-text">{employeeName ? `${employeeName} · ` : ""}{dateStr}</div>
            {holidayName && (
              <div className="text-[11px] text-warn mt-0.5 flex items-center gap-1">
                <Sun size={11} /> Public holiday — {holidayName}. Not worked = 7.6 h public holiday (ordinary). Worked = every hour double time.
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-ink-hover text-text-muted">
            <X size={16} />
          </button>
        </div>
        <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
          {entries.length === 0 && (
            <div className="text-[12px] text-text-muted">No hours logged for this day yet.</div>
          )}
          {entries.map(e => {
            const where = e.leave_request_id ? "Leave" : e.project_id ? "Site" : "Depot";
            const client = e.projects?.client_id ? clientById.get(e.projects.client_id) : null;
            return (
              <div key={e.entry_id} className="bg-ink-raised border border-line rounded p-3 text-[12px]">
                <div className="flex items-start justify-between gap-2 flex-wrap mb-2">
                  <div>
                    <div className="font-mono text-text">
                      {e.leave_type_code ? (e.notes ?? e.leave_type_code) : e.projects?.project_code ? `${e.projects.project_code}` : "— DEPOT —"}
                      {client && <span className="text-text-muted"> · {client}</span>}
                    </div>
                    {e.projects?.project_name && (
                      <div className="text-[11px] text-text-muted">{e.projects.project_name}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {(e as any).is_night && <Pill label="night 2×" tone="warning" small />}
                    {e.leave_type_code === "PUB"
                      ? <Pill label="public holiday" tone="warning" small />
                      : e.leave_type_code
                      ? <Pill label={Number(e.total_hours) > 0 ? "paid leave" : "leave · unpaid"} tone="notice" small />
                      : <Pill label={where.toLowerCase()} tone={where === "Site" ? "approved" : "default"} small />}
                    {e.submitted_at && <Pill label="submitted" tone="pending" small />}
                    {e.approved_at && <Pill label="approved" tone="approved" small />}
                    {/* mig 443 — they worked the holiday after all: take the PUB day off so the hours can go on */}
                    {canAdd && e.leave_type_code === "PUB" && e.leave_request_id && (
                      <button type="button" disabled={removingPub}
                        onClick={() => {
                          if (!window.confirm(`Remove the public-holiday day for ${employeeName || "this person"}?\n\nDo this only if they actually worked the holiday — their hours then go on as normal and every hour pays double time.`)) return;
                          onRemovePub(e.leave_request_id!, "Worked the public holiday");
                        }}
                        title="They worked the holiday — remove the paid public-holiday day"
                        className="px-1.5 text-[11px] text-text-muted hover:text-danger disabled:opacity-40">remove</button>
                    )}
                    {isDirector && e.approved_at && !e.leave_request_id && !e.leave_type_code && (
                      <button type="button" disabled={unapproving}
                        onClick={() => {
                          const reason = window.prompt(`Un-approve this ${(Number(e.total_hours) || 0).toFixed(2)}h entry? It goes back to draft and the reason goes on the record.\n\nReason:`);
                          if (reason === null) return;
                          if (!reason.trim()) { window.alert("A reason is required."); return; }
                          onUnapprove(e.entry_id, reason.trim());
                        }}
                        title="Director: take this approved entry back to draft"
                        className="px-1.5 text-[11px] text-text-muted hover:text-danger disabled:opacity-40">un-approve</button>
                    )}
                    {canAdd && !e.submitted_at && !e.approved_at && (
                      <button type="button" disabled={removing}
                        onClick={() => {
                          if (!window.confirm(`Remove this ${(Number(e.total_hours) || 0).toFixed(2)}h entry? It has not been submitted.`)) return;
                          onRemove(e.entry_id);
                        }}
                        title="Remove this draft entry"
                        className="px-1.5 text-[13px] text-text-muted hover:text-danger disabled:opacity-40">×</button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                  <Stat label="Start"  value={e.start_time ?? "—"} />
                  <Stat label="Finish" value={e.end_time ?? "—"} />
                  <Stat label="Break"  value={e.break_minutes ? `${e.break_minutes}m` : "—"} />
                  <Stat label="Total"  value={`${(Number(e.total_hours) || 0).toFixed(2)}h`} accent />
                  <Stat label="STD"    value={(Number(e.hours_std) || 0).toFixed(1)} />
                  <Stat label="1.5x"   value={(Number(e.hours_th)  || 0).toFixed(1)} />
                  <Stat label="2x"     value={(Number(e.hours_dt)  || 0).toFixed(1)} />
                  <Stat label="LAFHA"  value={e.lafha_nights ? `${e.lafha_nights}n` : "—"} />
                </div>
                {(e.travel_km != null || e.travel_hours != null) && (
                  <div className="mt-2 text-[11px] text-text-muted">
                    <span className="font-mono text-accent">🚗</span>{" "}
                    {e.travel_km != null ? `${e.travel_km} km` : ""}
                    {e.travel_km != null && e.travel_hours != null ? " · " : ""}
                    {e.travel_hours != null ? `${e.travel_hours}h` : ""}
                  </div>
                )}
                {e.notes && (
                  <div className="mt-2 text-[11px] text-text-muted italic">{e.notes}</div>
                )}
                {/* mig 421 — who put it in, and when */}
                <div className="mt-2 text-[11px] text-text-dim">
                  Entered by <span className="text-text-muted">{e.created_by ? (byUser[e.created_by]?.name ?? "someone") : "—"}</span>
                  {e.created_at ? <> · {format(parseISO(e.created_at), "d MMM HH:mm")}</> : null}
                  {e.mirrored_from_employee_id ? <> · copied by Fill gaps</> : null}
                  {e.approved_at ? <> · approved {format(parseISO(e.approved_at), "d MMM HH:mm")}</> : null}
                </div>
                {e.rejection_notes && (
                  <div className="mt-1 text-[11px] text-warn">{e.rejection_notes}</div>
                )}
              </div>
            );
          })}

          {/* mig 421 — lodge leave for this person on this day (manager+) */}
          {canAdd && !alreadyLeave && (
            <div className="border border-line rounded p-3 bg-ink-raised">
              <div className="text-[12px] font-medium text-text mb-2">Lodge leave for this day</div>
              {approvedWork.length > 0 ? (
                <div className="text-[11px] text-text-muted">
                  This day has approved hours on it. {isDirector ? "Un-approve and remove them first, then lodge the leave." : "A director has to un-approve them first."}
                </div>
              ) : (
                <>
                  {/* mig 443 — on a public holiday the normal case is one click */}
                  {holidayName && isWeekday && (
                    <div className="mb-2 flex items-center justify-between gap-2 flex-wrap bg-warn/10 border border-warn/40 rounded px-3 py-2">
                      <span className="text-[11px] text-text">
                        <span className="text-warn font-medium">{holidayName}</span> — not worked? Put the paid public holiday on (7.6 h, ordinary).
                        {entries.some(e => !e.leave_type_code && Number(e.total_hours) > 0) ? " Their logged hours on this day are replaced." : ""}
                      </span>
                      <Button size="sm" disabled={lodging}
                        onClick={() => onLodgeLeave("PUB", `Public holiday — ${holidayName}`)}>
                        {lodging ? "Putting on…" : "Public holiday (7.6 h)"}
                      </Button>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <select value={leaveCode} onChange={e => setLeaveCode(e.target.value)}
                      className="px-2 py-1.5 rounded border border-line bg-ink-card text-text text-[12px]">
                      <option value="">Leave type…</option>
                      {pickableLeaveTypes.map(t => (
                        <option key={t.leave_type_code} value={t.leave_type_code}>{t.display_name}{t.is_paid ? "" : " (unpaid)"}</option>
                      ))}
                    </select>
                    <input value={leaveReason} onChange={e => setLeaveReason(e.target.value)} placeholder="Reason (optional) — e.g. called in sick"
                      className="md:col-span-2 px-2 py-1.5 rounded border border-line bg-ink-card text-text text-[12px]" />
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-[11px] text-text-dim">Lodged on their behalf and approved now — paid leave books their day (7.6 h) as ordinary, unpaid books 0 h. Any draft hours on the day are replaced.</span>
                    <Button size="sm" disabled={lodging || !leaveCode}
                      onClick={() => { onLodgeLeave(leaveCode, leaveReason.trim()); setLeaveCode(""); setLeaveReason(""); }}>
                      {lodging ? "Lodging…" : "Lodge + approve"}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        {canAdd && (
          <div className="p-4 border-t border-line bg-ink-raised/40">
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono mb-2">
              Quick add{employeeName ? ` for ${employeeName}` : ""}
            </div>
            <div className="flex items-end gap-2 flex-wrap">
              <label className="flex-1 min-w-[160px]">
                <span className="text-[10px] text-text-muted">Project</span>
                <select value={projectId} onChange={e => setProjectId(e.target.value)}
                  className="mt-1 w-full bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text">
                  <option value="">— Depot / no project —</option>
                  {projects.map(pr => (
                    <option key={pr.project_id} value={pr.project_id}>
                      {pr.project_code}{pr.project_name ? ` — ${pr.project_name}` : ""}
                    </option>
                  ))}
                </select>
              </label>
              <label className="w-20">
                <span className="text-[10px] text-text-muted">Hours</span>
                <input type="number" value={hours} onChange={e => setHours(e.target.value)}
                  className="mt-1 w-full bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text text-right font-mono" />
              </label>
              <label className="inline-flex items-center gap-1.5 text-[12px] text-text-muted pb-1.5">
                <input type="checkbox" className="accent-accent" checked={taNight} onChange={e => setTaNight(e.target.checked)} />
                TA night
              </label>
              <label className="flex items-center gap-1.5 text-[12px] text-text whitespace-nowrap" title="Night works — every hour on the entry pays double time (mig 350)">
                <input type="checkbox" className="accent-accent" checked={isNight} onChange={e => setIsNight(e.target.checked)} />
                Night 2×
              </label>
              <Button size="sm" onClick={submitAdd} disabled={adding}>{adding ? "Adding…" : "Add"}</Button>
            </div>
            {err && <div className="text-[11px] text-danger mt-2">{err}</div>}
            <div className="text-[10px] text-text-dim mt-2">
              Books to 1010-LAB labour · STD/OT split auto-calculated. Logged as added by you.
              {holidayName ? <span className="text-warn"> Public holiday — every hour added here pays double time.</span> : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider2 text-text-muted font-mono">{label}</div>
      <div className={`font-mono ${accent ? "text-accent" : "text-text"}`}>{value}</div>
    </div>
  );
}
